import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Target, Heart, Sparkles, Shield, Users, Award, GraduationCap, Lightbulb } from 'lucide-react';
const pillars = [{
  id: 'mission',
  icon: Target,
  label: 'Our Mission',
  title: 'Real estate investment opportunities — for Nigerians at home and abroad.',
  desc: 'To provide real estate investment opportunities to individuals in Nigeria and the diaspora through our well-structured payment plans and exceptional service.',
  accent: 'Since 2018'
}, {
  id: 'vision',
  icon: Eye,
  label: 'Our Vision',
  title: 'Homeownership, attainable for every individual in need.',
  desc: 'We strive to make homeownership attainable for every individual in need.',
  accent: 'Vision 2030'
}, {
  id: 'values',
  icon: Heart,
  label: 'Core Values',
  title: 'Six principles. One promise.',
  desc: 'Passion for excellence, accountability, collaboration, integrity, learning, and innovation — these are the principles that shape every decision we make and every estate we deliver.',
  accent: '6 Pillars'
}];
const coreValues = [{
  icon: Sparkles,
  label: 'Passion for Excellence',
  desc: 'Uncompromising standards in every detail.'
}, {
  icon: Shield,
  label: 'Accountability',
  desc: 'We own outcomes — every promise, every plot.'
}, {
  icon: Users,
  label: 'Collaboration',
  desc: 'Partnership-led, community-driven delivery.'
}, {
  icon: Award,
  label: 'Integrity',
  desc: 'Transparent processes, verified titles.'
}, {
  icon: GraduationCap,
  label: 'Learning',
  desc: 'Forever curious, forever improving.'
}, {
  icon: Lightbulb,
  label: 'Innovation',
  desc: 'Forward-thinking solutions for modern living.'
}];
export function Mission() {
  const [active, setActive] = useState(0);
  const current = pillars[active];
  const Icon = current.icon;
  return <section className="py-24 md:py-32 px-6 md:px-12 max-w-[1280px] mx-auto bg-bone" data-id="element-560">
      <div className="text-center mb-16 max-w-2xl mx-auto" data-id="element-561">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-562">
          Who We Are
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-4xl md:text-5xl font-serif text-ink tracking-tight" data-id="element-563">
          Driven by purpose, built on trust.
        </motion.h2>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-10" data-id="element-564">
        {pillars.map((p, i) => {
        const PIcon = p.icon;
        const isActive = active === i;
        return <button key={p.id} onClick={() => setActive(i)} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${isActive ? 'bg-ink text-white shadow-lg' : 'bg-white text-ink/60 border border-ink/10 hover:border-ink/30'}`} data-id="element-565">
              <PIcon className={`w-4 h-4 ${isActive ? 'text-gold' : ''}`} data-id="element-566" />
              {p.label}
            </button>;
      })}
      </div>

      {/* Active panel */}
      <div className="relative bg-white rounded-3xl border border-ink/5 overflow-hidden min-h-[360px]" data-id="element-567">
        <AnimatePresence mode="wait" data-id="element-568">
          <motion.div key={current.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="grid grid-cols-1 md:grid-cols-12 gap-0" data-id="element-569">
            {/* Left: Visual */}
            <div className="md:col-span-5 bg-ink text-white p-10 md:p-12 relative overflow-hidden flex flex-col justify-between min-h-[360px]" data-id="element-570">
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 30,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full border border-gold/20 pointer-events-none" data-id="element-571" />
              <motion.div animate={{
              rotate: -360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute -top-20 -left-20 w-60 h-60 rounded-full border border-gold/10 pointer-events-none" data-id="element-572" />

              <div className="relative" data-id="element-573">
                <div className="w-16 h-16 rounded-2xl bg-gold flex items-center justify-center mb-6 shadow-xl" data-id="element-574">
                  <Icon className="w-8 h-8 text-white" strokeWidth={1.5} data-id="element-575" />
                </div>
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-gold mb-3" data-id="element-576">
                  {current.label}
                </div>
              </div>

              <div className="relative" data-id="element-577">
                <div className="text-xs text-white/40 font-mono tracking-widest uppercase" data-id="element-578">
                  {current.accent}
                </div>
              </div>
            </div>

            {/* Right: Text */}
            <div className="md:col-span-7 p-10 md:p-12 flex flex-col justify-center" data-id="element-579">
              <h3 className="text-3xl md:text-4xl font-serif text-ink leading-[1.15] mb-6 max-w-xl" data-id="element-580">
                {current.title}
              </h3>
              <p className="text-ink/65 text-base md:text-lg leading-relaxed max-w-xl" data-id="element-581">
                {current.desc}
              </p>

              <div className="flex items-center gap-2 mt-10" data-id="element-582">
                {pillars.map((_, i) => <button key={i} onClick={() => setActive(i)} className={`h-1 rounded-full transition-all ${i === active ? 'w-10 bg-gold' : 'w-2 bg-ink/15 hover:bg-ink/30'}`} aria-label={`Pillar ${i + 1}`} data-id="element-583" />)}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Core values — 6 pillars grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6" data-id="element-584">
        {coreValues.map((v, i) => <motion.div key={v.label} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.06
      }} className="bg-white rounded-2xl p-5 border border-ink/5 hover:border-gold/30 hover:shadow-md transition-all group" data-id="element-585">
            <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center mb-3 group-hover:bg-gold/20 transition-colors" data-id="element-586">
              <v.icon className="w-5 h-5 text-gold-dark group-hover:scale-110 transition-transform" data-id="element-587" />
            </div>
            <div className="font-serif text-lg text-ink mb-1" data-id="element-588">{v.label}</div>
            <div className="text-xs text-ink/55 leading-relaxed" data-id="element-589">{v.desc}</div>
          </motion.div>)}
      </div>
    </section>;
}